import { useEffect } from 'react';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { XCircleIcon, ArrowLeftIcon, HomeIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

export default function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    // Log error to your error tracking service
    console.error('Application error:', error);
  }, [error]);

  const getErrorMessage = () => {
    if (isRouteErrorResponse(error)) {
      switch (error.status) {
        case 404:
          return t('common.error404', "The page you're looking for doesn't exist.");
        case 401:
          return t('common.error401', "You're not authorized to access this page.");
        case 403:
          return t('common.error403', "You don't have permission to access this page.");
        case 503:
          return t('common.error503', "This service is currently unavailable.");
        default:
          return error.statusText || t('common.errorGeneric', 'Something went wrong.');
      }
    }
    if (error instanceof Error) {
      return error.message;
    }
    return t('common.errorUnexpected', 'An unexpected error occurred.');
  };

  const getErrorTitle = () => {
    if (isRouteErrorResponse(error)) {
      return `${error.status} - ${error.statusText}`;
    }
    return t('common.applicationError', 'Application Error');
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      theme === 'dark' ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'
    }`}>
      <div className={`max-w-md w-full p-8 rounded-lg shadow-lg ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="flex items-center gap-4 mb-6">
          <XCircleIcon className="w-10 h-10 text-red-500 flex-shrink-0" />
          <h1 className="text-2xl font-semibold">{getErrorTitle()}</h1>
        </div>

        <p className={`mb-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          {getErrorMessage()}
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-100'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
            }`}
          >
            <ArrowLeftIcon className="w-5 h-5" />
            {t('common.goBack', 'Go Back')}
          </button>
          <button
            onClick={() => navigate('/')}
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            <HomeIcon className="w-5 h-5" />
            {t('common.goHome', 'Go Home')}
          </button>
        </div>
      </div>
    </div>
  );
}
